import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { BehaviorSubject, filter, tap } from "rxjs";
import { debounceTime } from "rxjs/operators";
import ImperativeTooltip from "./ImperativeTooltip";
import eventEmitter from "../../eventEmitter";
import isWithinClass from "../../../common/helpers/isWithinClass";

let idCounter = 0;

function HoverClickTooltip({
  children,
  ContentRenderer,
  delay,
  disabled,
  className,
  shouldFollowMouse,
  HtmlTag,
  HtmlTagClassName,
  ...rest
}) {
  const [isHovered, setHovered] = useState(false);
  const anchorRef = useRef(null);
  const idCounterRef = useRef(`tooltip-${(idCounter += 1)}`);
  const idAnchorRef = useRef(`anchor-${(idCounter += 1)}`);
  const bridgeAnchor = useRef(null);
  const tooltipAnchor = useRef(null);
  const hoverEvent$ = useRef(new BehaviorSubject(false));
  const bridgeCl = isHovered ? "tw-block" : "tw-hidden";

  useEffect(() => {
    let isVisible = false;
    let isMouseOver = false;

    const sub1 = hoverEvent$.current
      .pipe(
        filter((ev) => ev && !disabled),
        tap(() => (isMouseOver = true)),
        debounceTime(delay)
      )
      .subscribe((ev) => {
        bridgeAnchor.current.style.left = `${ev.clientX}px`;
        bridgeAnchor.current.style.top = `${ev.clientY}px`;
        tooltipAnchor.current.style.left = `${ev.clientX}px`;
        tooltipAnchor.current.style.top = `${ev.clientY - 8}px`;

        isVisible = isMouseOver;
        setHovered(isVisible);
      });

    const sub2 = eventEmitter.mouseMoveTracker$
      .pipe(filter((ev) => isMouseOver && ev))
      .subscribe((ev) => {
        const el = anchorRef.current;
        const isTooltipHover = isWithinClass(ev.target, idCounterRef.current);
        const isAnchorHover = isWithinClass(ev.target, idAnchorRef.current);
        const isElHover = (el && el.contains(ev.target)) || el === ev.target;

        isMouseOver = isTooltipHover || isAnchorHover || isElHover;
        isVisible = isVisible && isMouseOver;

        setHovered(isVisible);
      });

    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
    };
  }, [disabled, delay]);

  return (
    <>
      <div
        ref={bridgeAnchor}
        className={`tw-fixed tw-transform tw--translate-y-full tw--translate-x-1/2 tw-h-4 tw-w-6 ${bridgeCl} ${idAnchorRef.current}`}
      />

      <div
        ref={tooltipAnchor}
        className="tw-fixed tw-transform tw-pointer-events-none tw--translate-x-1/2 tw--translate-y-1/2 tw-h-1 tw-w-1"
      />

      {isHovered && !disabled ? (
        <ImperativeTooltip
          followMouse={false}
          anchor={tooltipAnchor.current}
          shouldFollowObjectOnScroll={false}
          ContentRenderer={ContentRenderer}
          className={`${className} ${idCounterRef.current}`}
          {...rest}
        />
      ) : null}

      <HtmlTag
        ref={anchorRef}
        onMouseMove={(ev) => hoverEvent$.current.next(ev)}
        onMouseEnter={(ev) => hoverEvent$.current.next(ev)}
        className={HtmlTagClassName}
      >
        {children}
      </HtmlTag>
    </>
  );
}

HoverClickTooltip.propTypes = {
  HtmlTag: PropTypes.string,
  shouldFollowMouse: PropTypes.bool,
  children: PropTypes.element,
  ContentRenderer: PropTypes.func.isRequired,
  delay: PropTypes.number,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  HtmlTagClassName: PropTypes.string,
};

HoverClickTooltip.defaultProps = {
  HtmlTag: "div",
  HtmlTagClassName: "tw-inline",
  shouldFollowMouse: false,
  children: null,
  delay: 300,
  disabled: false,
  className: "",
};

export default HoverClickTooltip;
